'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTutorialOptional } from '@/features/tutorial/TutorialContext';
import {
  BoardStageProvider,
  type BoardStageAnimationApi,
  type BoardStageContextValue,
  type BoardStagePhase,
} from './board-stage-context';
import { BOARD_PRELOAD_TIMEOUT_MS } from './board-stage-timings';
import { BoardStageSlot, type BoardStageSlotProps } from './BoardStageSlot';

/** Strict subset of `BoardStageSlotProps` the stage forwards into
 *  every slot it mounts. The stage controls `boardId`, `phase`,
 *  `isActiveTarget`, and `onReady` itself; everything else is
 *  pass-through from `LobbyClient`. */
export type LobbyBoardStageRenderProps = Omit<
  BoardStageSlotProps,
  'boardId' | 'phase' | 'isActiveTarget' | 'onReady' | 'bypassReadinessGate'
>;

export interface LobbyBoardStageProps extends LobbyBoardStageRenderProps {
  /** The board id from the URL (= `activeBoardId` in `LobbyClient`).
   *  When this changes, the stage mounts a new slot offscreen, waits
   *  for it to signal readiness, then crossfades. */
  targetBoardId: string;
  /** Tree the stage exposes its context to. The actual canvas mount
   *  point is `<LobbyBoardStageCanvas />`, which a descendant places
   *  into the canvas region of `LobbyClient`. Drawers / dev panels
   *  / chip-rail reset all consume `useBoardStageContext()` from this
   *  same children subtree. */
  children: ReactNode;
}

/**
 * Tracks the "we have a slot mounted for this id" lifecycle. The
 * `key` is what React keys the slot subtree on — bumping it forces
 * a fresh mount with empty state, even when the same `boardId` is
 * re-targeted (e.g. user navigates A → B → A — A's second visit
 * should not inherit A's previous `tileAnimStates`).
 */
interface SlotState {
  /** The board this slot is rendering. */
  boardId: string;
  /** Mount-stable key. Two slots with the same boardId can coexist
   *  briefly during a rapid retarget; bumping the key on each new
   *  occurrence keeps React from accidentally reusing one. */
  key: string;
  /** Visible lifecycle phase the slot is in. */
  phase: BoardStagePhase;
}

/**
 * Internal wiring between the state machine in `<LobbyBoardStage>`
 * and the `<LobbyBoardStageCanvas>` child that renders the actual
 * slot pair. Kept private to this module — outside callers go
 * through `useBoardStageContext()` for the public surface.
 */
interface BoardStageInternalValue {
  slots: { current: SlotState; incoming: SlotState | null };
  handleIncomingReady: () => void;
  isTutorialMode: boolean;
  renderProps: LobbyBoardStageRenderProps;
}

const BoardStageInternalContext =
  createContext<BoardStageInternalValue | null>(null);

/**
 * Two-slot preload + hard-cut orchestrator for the lobby canvas.
 *
 * The incoming board is mounted offscreen and painted before it is
 * ever shown, so the user never sees an empty / half-loaded board.
 * Once it's ready the swap is an instant hard cut — no opacity
 * crossfade and no audio.
 *
 * ## Lifecycle
 *
 * 1. **Cold mount.** The stage mounts a single slot with
 *    `phase='current'`. The slot signals `onReady` once its data +
 *    first paint settle; the stage does nothing with that signal
 *    because the cold-mount slot is already visible.
 * 2. **Target change.** When `targetBoardId` changes, the stage
 *    mounts a SECOND slot with `phase='incoming-loading'` (opacity
 *    0, offscreen). The first slot keeps painting at
 *    `phase='current'`. We arm a hard ceiling timer at
 *    `BOARD_PRELOAD_TIMEOUT_MS` so a stuck indexer / network hiccup
 *    can't wedge the user.
 * 3. **Readiness.** When the incoming slot's `onReady` fires (data
 *    loaded + first frame painted) the stage commits the swap
 *    synchronously: the incoming slot flips to `phase='current'`
 *    and the outgoing slot unmounts in the same render. No fade,
 *    no timers, no sound.
 *
 * ## Rapid retarget mid-preload
 *
 * If `targetBoardId` changes while an incoming slot is still
 * preloading:
 *   - The current incoming slot is replaced (new key) with one for
 *     the new id — its readiness gate restarts;
 *   - The visible slot keeps painting (the user still sees the
 *     previously-current board) until the latest target is ready.
 *
 * ## Tutorial
 *
 * - Tutorial mode bypasses the readiness gate so any tutorial-driven
 *   id change cuts over immediately. The script doesn't fetch live
 *   data and would deadlock waiting on `gridLoading=false`.
 */
export function LobbyBoardStage({
  targetBoardId,
  children,
  ...renderProps
}: LobbyBoardStageProps) {
  const tutorial = useTutorialOptional();
  const isTutorialMode = !!tutorial;

  // Monotonic counter for slot keys. Each new slot pushed onto the
  // stage gets `slotKeyCounter++`, so a board id revisited later
  // can never collide with a previous slot's React subtree.
  const slotKeyCounterRef = useRef(0);
  const newSlotKey = useCallback((boardId: string) => {
    const next = ++slotKeyCounterRef.current;
    return `${boardId}:${next}`;
  }, []);

  const [slots, setSlots] = useState<{
    current: SlotState;
    incoming: SlotState | null;
  }>(() => ({
    current: {
      boardId: targetBoardId,
      key: `${targetBoardId}:0`,
      phase: 'current',
    },
    incoming: null,
  }));

  // Latest committed `displayedBoardId` exposed via context. Always
  // tracks the slot in `phase='current'` (or the outgoing one mid-
  // fade — same value, just a snapshot).
  const displayedBoardId = slots.current.boardId;
  const isTransitioning = slots.incoming !== null;

  // Active slot animation API — installed by whichever slot has
  // `isActiveTarget=true`. The ref is mutated synchronously inside
  // the `__registerActiveSlot` cleanup so a slot that just lost
  // active status can't keep handling drawer dispatches.
  const activeApiRef = useRef<BoardStageAnimationApi | null>(null);

  // ─── Stage context surface ────────────────────────────────────────
  const stageValue = useMemo<BoardStageContextValue>(
    () => ({
      displayedBoardId,
      interactionsLocked: isTransitioning,
      playBuyoutAnimation: (seatIndex, prev, next) => {
        activeApiRef.current?.playBuyoutAnimation(seatIndex, prev, next);
      },
      playClaimAnimation: (seatIndex, next) => {
        activeApiRef.current?.playClaimAnimation(seatIndex, next);
      },
      triggerTileAnim: (kind, count) => {
        activeApiRef.current?.triggerTileAnim(kind, count);
      },
      __registerActiveSlot: (api) => {
        activeApiRef.current = api;
        return () => {
          if (activeApiRef.current === api) {
            activeApiRef.current = null;
          }
        };
      },
    }),
    [displayedBoardId, isTransitioning],
  );

  // ─── Tutorial bridge wiring ───────────────────────────────────────
  // The tutorial overlay registers a single bridge for the lifetime
  // of a tutorial run; we install a permanent forwarder here so the
  // bridge stays valid across slot swaps. Calls land on whichever
  // slot is currently active. Uses `useEffect` rather than the slot's
  // own `isActiveTarget` flow so we don't churn the tutorial's ref
  // on every navigation.
  useEffect(() => {
    if (!tutorial) return;
    return tutorial.registerTileAnimBridge((seatIndex, state) => {
      activeApiRef.current?.setSingleTileAnim(seatIndex, state);
    });
  }, [tutorial]);

  // ─── Crossfade orchestration ──────────────────────────────────────
  /**
   * Begin (or update) a transition toward `nextBoardId`.
   *
   * Called from a `useEffect` that observes `targetBoardId`. When
   * the stage is already idle the new slot is mounted offscreen
   * and the readiness handshake takes over. When a preload is
   * already in flight, we replace the incoming slot in-place so
   * the user always cuts over to the *latest* requested board
   * even if they spam-clicked through multiple cards.
   */
  const startTransitionTo = useCallback(
    (nextBoardId: string) => {
      setSlots((prev) => {
        if (
          prev.current.boardId === nextBoardId &&
          prev.incoming === null
        ) {
          return prev;
        }
        // Same board re-targeted while a preload was already in
        // flight — no-op, the in-flight preload will cut over to
        // the right id. (A new mount would discard its readiness
        // gate for nothing.)
        if (prev.incoming?.boardId === nextBoardId) {
          return prev;
        }
        // Reverting back to the current board mid-preload: cancel
        // the incoming slot so the user stays on the original
        // board. Covers the A → B (mid-preload) → A user gesture.
        if (
          prev.incoming !== null &&
          prev.current.boardId === nextBoardId
        ) {
          return {
            current: { ...prev.current, phase: 'current' },
            incoming: null,
          };
        }
        // Replace the incoming slot (or create one if the stage
        // was idle). Bump the key so the slot subtree is fresh.
        return {
          current: prev.current,
          incoming: {
            boardId: nextBoardId,
            key: newSlotKey(nextBoardId),
            phase: 'incoming-loading',
          },
        };
      });
    },
    [newSlotKey],
  );

  // Drive transitions off the URL-active board id.
  useEffect(() => {
    startTransitionTo(targetBoardId);
  }, [targetBoardId, startTransitionTo]);

  // ─── Readiness gate / hard-cut commit ────────────────────────────
  /**
   * Commit the swap: the incoming slot becomes `current` and the
   * outgoing slot unmounts in the same render. An instant hard cut,
   * no fade and no audio. Centralised here so the timeout-based
   * fallback and the real readiness signal both land through the
   * same path.
   */
  const commitIncoming = useCallback(() => {
    setSlots((prev) => {
      if (!prev.incoming) return prev;
      if (prev.incoming.phase !== 'incoming-loading') return prev;
      return {
        current: { ...prev.incoming, phase: 'current' },
        incoming: null,
      };
    });
  }, []);

  // Capture the latest commitIncoming in a ref so the slot's
  // `onReady` prop has a stable identity. The slot's readiness
  // useEffect deps include `onReady`; if the prop identity churns,
  // the gate could re-fire and commit twice. With a ref the prop
  // stays stable for the slot's lifetime.
  const commitIncomingRef = useRef(commitIncoming);
  commitIncomingRef.current = commitIncoming;
  const handleIncomingReady = useCallback(() => {
    commitIncomingRef.current();
  }, []);

  const incomingKey = slots.incoming?.key ?? null;
  const incomingPhase = slots.incoming?.phase ?? null;

  // ─── Hard-cut fallback (preload timeout / tutorial) ──────────────
  // If readiness hasn't fired by `BOARD_PRELOAD_TIMEOUT_MS`, force
  // the swap. Better a hard cut than wedging the user on the
  // outgoing board. Tutorial mode commits synchronously because the
  // script doesn't fetch live data.
  useEffect(() => {
    if (!incomingKey) return;
    if (incomingPhase !== 'incoming-loading') return;
    if (isTutorialMode) {
      const t = window.setTimeout(() => commitIncoming(), 0);
      return () => window.clearTimeout(t);
    }
    const timeout = window.setTimeout(() => {
      commitIncoming();
    }, BOARD_PRELOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, [incomingKey, incomingPhase, isTutorialMode, commitIncoming]);

  // ─── Internal context for `<LobbyBoardStageCanvas>` ──────────────
  // The internal context exists so LobbyClient's render can place
  // the slots into the canvas region while the public stage context
  // wraps the full surrounding tree (drawers, dev panels, chip rail
  // reset). `renderProps` change every commit because the parent
  // recomputes them; that's the desired behaviour — the slot pair
  // re-renders with the latest tile-click handler / sticky tooltip /
  // etc.
  const internalValue = useMemo<BoardStageInternalValue>(
    () => ({
      slots,
      handleIncomingReady,
      isTutorialMode,
      renderProps,
    }),
    [slots, handleIncomingReady, isTutorialMode, renderProps],
  );

  return (
    <BoardStageProvider value={stageValue}>
      <BoardStageInternalContext.Provider value={internalValue}>
        {children}
      </BoardStageInternalContext.Provider>
    </BoardStageProvider>
  );
}

/**
 * Renders the actual two-slot crossfade DOM. Mount this as a
 * descendant of `<LobbyBoardStage>` at the spot in the layout
 * where the canvas should appear. The component is intentionally
 * unstyled at the wrapper level — its inner `absolute inset-0`
 * div fills whatever positioned ancestor it ends up inside, just
 * like the original canvas mount block did.
 */
export function LobbyBoardStageCanvas() {
  const internal = useContext(BoardStageInternalContext);
  if (!internal) {
    throw new Error(
      '<LobbyBoardStageCanvas /> must be rendered inside <LobbyBoardStage>',
    );
  }
  const { slots, handleIncomingReady, isTutorialMode, renderProps } = internal;

  // Active-target rules:
  //   • Idle (one slot)      → that slot is the active target.
  //   • Transitioning         → the incoming slot is the active
  //                             target. The user has navigated to
  //                             its boardId; the outgoing slot is
  //                             on borrowed time, so drawer-driven
  //                             animations should land on the new
  //                             one.
  const currentIsActive = !slots.incoming;
  const incomingIsActive = !!slots.incoming;

  return (
    <div className="absolute inset-0">
      <BoardStageSlot
        key={slots.current.key}
        boardId={slots.current.boardId}
        phase={slots.current.phase}
        isActiveTarget={currentIsActive}
        bypassReadinessGate={isTutorialMode}
        {...renderProps}
      />
      {slots.incoming ? (
        <BoardStageSlot
          key={slots.incoming.key}
          boardId={slots.incoming.boardId}
          phase={slots.incoming.phase}
          isActiveTarget={incomingIsActive}
          onReady={handleIncomingReady}
          bypassReadinessGate={isTutorialMode}
          {...renderProps}
        />
      ) : null}
    </div>
  );
}
